import {
  GCRContentConnectionManagementClient,
  ListCollectionsCommand,
  ListUseCasesCommand,
  GetCollectionDetailCommand,
  GetUseCaseDetailCommand,
  CreateCollectionCommand,
  UpdateCollectionCommand,
  CreateSubscriptionCommand,
  CancelSubscriptionCommand,
  type Collection,
  type UseCase,
  type GetCollectionDetailInput,
  type GetUseCaseDetailInput,
  type CreateCollectionInput,
  type UpdateCollectionInput,
  type CreateSubscriptionInput,
  type CreateSubscriptionOutput,
  type CancelSubscriptionInput,
  type CancelSubscriptionOutput,
} from '@amzn/aws-gcr-content-connection-management-client';
import { getConnectionManagementEndpoint } from '../utils/endpointUtil';
import { getStageFromUrl } from '../utils/stageUtil';
import type { Region } from '../shared/contexts/RegionContext';
import { CONNECTION_AUTH_CONFIG, APP_CONFIG, getIdentityPoolId } from '../shared/config/authConfig';
import { createCognitoCredentialProvider } from '../shared/auth/cognitoCredentials';
import { getFederateToken } from '../shared/auth/federateAuth';

// Build a Connection Management client for the given region. When a Cognito Identity Pool is
// configured for the current region + stage (SCE-11405), attach a Cognito-brokered credential
// provider so the SDK SigV4-signs requests (service=execute-api) against the API. This is inert
// while the API is still authorizationType=NONE (the signature is ignored) and becomes
// load-bearing once the API is flipped to AWS_IAM (Phase 2). Region+stage combinations whose pool
// id is not yet configured fall back to unsigned calls -- so signing enables per stage.
const buildClient = (region: Region): GCRContentConnectionManagementClient => {
  const { sigv4Region } = CONNECTION_AUTH_CONFIG[region];
  const identityPoolId = getIdentityPoolId(region, getStageFromUrl());
  return new GCRContentConnectionManagementClient({
    region: sigv4Region,
    endpoint: getConnectionManagementEndpoint(region),
    ...(identityPoolId ? { credentials: createCognitoCredentialProvider(region, identityPoolId, () => getFederateToken(APP_CONFIG)) } : {}),
  });
};

class ConnectionManagementService {
  private client: GCRContentConnectionManagementClient;
  private currentRegion: Region = 'global';

  constructor() {
    this.client = buildClient(this.currentRegion);
  }

  setRegion(region: Region) {
    this.currentRegion = region;
    this.client = buildClient(region);
  }

  async listCollections(): Promise<Collection[]> {
    const command = new ListCollectionsCommand({});
    const response = await this.client.send(command);
    return response.collections || [];
  }

  async getCollectionDetail(input: GetCollectionDetailInput): Promise<Collection> {
    const command = new GetCollectionDetailCommand(input);
    const response = await this.client.send(command);
    return response;
  }

  async listUseCases(): Promise<UseCase[]> {
    const command = new ListUseCasesCommand({});
    const response = await this.client.send(command);
    return response.useCases || [];
  }

  async getUseCaseDetail(input: GetUseCaseDetailInput): Promise<UseCase> {
    const command = new GetUseCaseDetailCommand(input);
    const response = await this.client.send(command);
    return response;
  }

  async createCollection(input: CreateCollectionInput): Promise<Collection> {
    const command = new CreateCollectionCommand(input);
    const response = await this.client.send(command);
    return response;
  }

  async updateCollection(input: UpdateCollectionInput): Promise<Collection> {
    const command = new UpdateCollectionCommand(input);
    const response = await this.client.send(command);
    return response;
  }

  async createSubscription(input: CreateSubscriptionInput): Promise<CreateSubscriptionOutput> {
    const command = new CreateSubscriptionCommand(input);
    const response = await this.client.send(command);
    return response;
  }

  async cancelSubscription(input: CancelSubscriptionInput): Promise<CancelSubscriptionOutput> {
    const command = new CancelSubscriptionCommand(input);
    const response = await this.client.send(command);
    return response;
  }
}

export const connectionManagementService = new ConnectionManagementService();
